import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* /smartket/board — Product Board (Phase 2).
   Now with search + filter + template + attribution + online-verifiable
   flag. Founders list. Marketers browse and pull slips. */

const TEMPLATES = [
  { id: "sprint",      label: "Sprint",        blurb: "90 days from activation, then closes.", best: "Digital downloads, one-time products." },
  { id: "steady",      label: "Steady Climb",  blurb: "12 mo full + 6 mo half taper on repeat customers.", best: "Most physical & SaaS products.", recommended: true },
  { id: "partnership", label: "Partnership",   blurb: "Up to 24 mo or $10k per customer, whichever first.", best: "Subscriptions / high LTV." },
];
const PLATFORMS = [
  { id: "stripe",       label: "Stripe Checkout" },
  { id: "shopify",      label: "Shopify" },
  { id: "gumroad",      label: "Gumroad" },
  { id: "woocommerce",  label: "WooCommerce" },
  { id: "etsy",         label: "Etsy" },
  { id: "other_online", label: "Other online platform" },
];
const TEMPLATE_LABEL = {
  sprint: "Sprint · 90d",
  steady: "Steady · 12+6mo",
  partnership: "Partnership · 24mo/$10k",
};

export default function SmartKetBoard() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [showForm, setShowForm] = useState(sp.get("list") === "1");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tpl, setTpl] = useState("");
  const [platform, setPlatform] = useState("");
  const [minPct, setMinPct] = useState(0);
  const [sort, setSort] = useState("newest");

  useEffect(() => { document.title = "SmartKet Product Board"; }, []);
  // Live-refetch on filter changes so the dropdowns feel instant.
  // The SEARCH button still works for the text query (which we don't
  // want firing on every keystroke).
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tpl, platform, sort, minPct]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const [loadErr, setLoadErr] = useState("");
  const load = async () => {
    setLoading(true); setLoadErr("");
    try {
      const params = { sort };
      if (q.trim()) params.q = q.trim();
      if (tpl) params.template = tpl;
      if (platform) params.platform = platform;
      if (minPct > 0) params.min_pct = minPct;
      const r = await axios.get(`${API}/smartket/listings/search`, { params });
      setListings(r.data?.listings || []);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || "couldn't load the board — refresh in a sec");
    }
    setLoading(false);
  };

  return (
    <div data-testid="smartket-board" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <Link to="/smartket/join" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
              ← SMARTKET
            </Link>
            <div className="flex gap-2 flex-wrap">
              <Link to="/smartket/marketers" data-testid="link-marketers"
                    className="bg-white/10 border border-white/20 hover:bg-white/20 font-mono text-[11px] tracking-widest px-3 py-2">
                MARKETERS ▸
              </Link>
              <Link to="/smartket/slips" data-testid="link-myslips"
                    className="bg-white/10 border border-white/20 hover:bg-white/20 font-mono text-[11px] tracking-widest px-3 py-2">
                MY SLIPS ▸
              </Link>
              {user && (
                <button
                  data-testid="board-list-btn"
                  onClick={() => setShowForm(v => !v)}
                  className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-4 py-2 shadow-brutal hover:bg-white transition-colors"
                >
                  {showForm ? "CLOSE" : "+ LIST YOUR PRODUCT"}
                </button>
              )}
            </div>
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-none tracking-tight">
            THE <span className="text-[#d4a34a]">PRODUCT BOARD</span>
          </h1>
          <p className="mt-3 font-mono text-sm text-white/75 max-w-2xl">
            Real founders. Real products. Pull a slip on one you can actually move — earn commission on every verified sale.
          </p>
        </div>
      </div>

      {showForm && user && (
        <div className="border-b border-white/10 bg-[#1a1410]">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
            <ListingForm onDone={() => { setShowForm(false); load(); }} />
          </div>
        </div>
      )}

      {/* SEARCH + FILTER BAR */}
      <div className="border-b border-white/10 bg-[#0d1410]/60">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="grid grid-cols-1 md:grid-cols-6 gap-2" data-testid="board-search-form">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="search products…"
                   data-testid="board-q" className="md:col-span-2 bg-black/40 border border-white/20 focus:border-[#d4a34a] px-3 py-2 text-white text-sm font-mono outline-none" />
            <select value={tpl} onChange={e => setTpl(e.target.value)} data-testid="board-tpl"
                    className="bg-black/40 border border-white/20 px-2 py-2 text-white text-sm font-mono">
              <option value="">All templates</option>
              {TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <select value={platform} onChange={e => setPlatform(e.target.value)} data-testid="board-plat"
                    className="bg-black/40 border border-white/20 px-2 py-2 text-white text-sm font-mono">
              <option value="">All platforms</option>
              {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)} data-testid="board-sort"
                    className="bg-black/40 border border-white/20 px-2 py-2 text-white text-sm font-mono">
              <option value="newest">Newest</option>
              <option value="highest_pct">Highest %</option>
              <option value="most_interest">Most interest</option>
            </select>
            <button type="submit" data-testid="board-search-btn"
                    className="bg-[#d4a34a] text-black font-mono text-[11px] tracking-widest hover:bg-white">
              SEARCH
            </button>
          </form>
          <div className="mt-3 flex items-center gap-3">
            <label className="font-mono text-[10px] tracking-widest text-white/60">MIN %</label>
            <input type="range" min={0} max={70} step={5} value={minPct} onChange={e => setMinPct(parseInt(e.target.value))}
                   onMouseUp={load} onTouchEnd={load} data-testid="board-minpct"
                   className="w-40 accent-[#d4a34a]" />
            <span className="font-mono text-xs text-[#d4a34a]">{minPct}%+</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {loadErr && (
          <div data-testid="board-load-err" className="mb-4 font-mono text-xs text-yt-red border border-yt-red/50 p-3">
            {loadErr}
          </div>
        )}
        {loading && <div className="font-mono text-sm text-white/60">loading the board…</div>}
        {!loading && listings.length === 0 && (
          <div className="border-2 border-dashed border-white/20 p-8 text-center">
            <div className="font-display text-2xl text-white/80 mb-2">Nothing matches your filter.</div>
            <p className="font-mono text-sm text-white/60 max-w-lg mx-auto">
              Loosen your filters, or be the first founder to list something in this niche.
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(l => <ListingCard key={l.id} l={l} user={user} />)}
        </div>
      </div>
    </div>
  );
}

function ListingCard({ l, user }) {
  const disabled = user && user._id === l.founder_id;
  return (
    <div data-testid={`listing-${l.id}`} className="border-2 border-white/10 bg-[#141a15] p-5 flex flex-col hover:border-[#d4a34a]/60 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] tracking-widest text-[#d4a34a]">
          {l.commission_pct}% COMMISSION
        </span>
        <span className="font-mono text-[10px] text-white/40">🌱 {l.founder_name}</span>
      </div>
      <div className="font-display text-xl md:text-2xl leading-tight mb-2">{l.title}</div>
      <p className="font-mono text-xs text-white/75 leading-relaxed mb-3 flex-1">{l.description}</p>
      <div className="font-mono text-[10px] text-white/60 mb-2">
        <span className="text-[#d4a34a]">TARGETS:</span> {l.target_audience}
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="font-mono text-[9px] tracking-widest bg-white/10 text-white/70 px-1.5 py-0.5 border border-white/10">
          {TEMPLATE_LABEL[l.template || "steady"]}
        </span>
        <span className="font-mono text-[9px] tracking-widest bg-white/10 text-white/70 px-1.5 py-0.5 border border-white/10">
          {(l.attribution_days || 60)}d WINDOW
        </span>
        {l.is_online_verifiable && (
          <span className="font-mono text-[9px] tracking-widest bg-[#3d7a4f]/20 text-[#7fd49b] px-1.5 py-0.5 border border-[#3d7a4f]/40">
            ✓ ONLINE
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <a href={l.url} target="_blank" rel="noreferrer" className="flex-1 text-center bg-white/10 border border-white/20 hover:bg-white/20 font-mono text-[11px] tracking-widest py-2 transition-colors">
          VIEW ↗
        </a>
        <Link
          to={`/smartket/slip?listing=${l.id}`}
          data-testid={`pull-slip-${l.id}`}
          className={`flex-1 text-center font-mono text-[11px] tracking-widest py-2 transition-colors ${
            disabled ? "bg-white/5 text-white/30 pointer-events-none" : "bg-[#d4a34a] text-black hover:bg-white"
          }`}
        >
          {disabled ? "YOUR LISTING" : "▸ PULL A SLIP"}
        </Link>
      </div>
    </div>
  );
}

function ListingForm({ onDone }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [desc, setDesc] = useState("");
  const [targ, setTarg] = useState("");
  const [pct, setPct] = useState(30);
  const [tpl, setTpl] = useState("steady");
  const [attrib, setAttrib] = useState(60);
  const [plat, setPlat] = useState("stripe");
  const [verifiable, setVerifiable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!verifiable) {
      setErr("SmartKet only supports online, trackable sales for launch. Cash/offline products can't be verified yet.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      const created = await axios.post(`${API}/smartket/listings`, {
        title, url, description: desc, target_audience: targ, commission_pct: pct,
      });
      const listingId = created.data?.id;
      if (listingId) {
        await axios.post(`${API}/smartket/listings/enhance`, {
          listing_id: listingId,
          template: tpl,
          attribution_days: attrib,
          sales_platform: plat,
          is_online_verifiable: true,
        });
      }
      onDone();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "couldn't save the listing");
    }
    setBusy(false);
  };

  const F = "w-full bg-black/40 border border-white/20 focus:border-[#d4a34a] px-3 py-2 text-white text-sm font-mono outline-none";

  return (
    <form onSubmit={submit} data-testid="listing-form" className="space-y-4">
      <div className="font-mono text-[10px] tracking-[0.4em] text-[#d4a34a]">LIST YOUR PRODUCT</div>
      {err && <div className="font-mono text-xs text-yt-red border border-yt-red/50 p-2">{err}</div>}

      <input value={title} onChange={e => setTitle(e.target.value)} required maxLength={100} placeholder="Product name" data-testid="lf-title" className={F} />
      <input value={url} onChange={e => setUrl(e.target.value)} required maxLength={500} placeholder="Live URL (https://...)" data-testid="lf-url" className={F} />
      <textarea value={desc} onChange={e => setDesc(e.target.value)} required maxLength={800} rows={3} placeholder="One paragraph: what it is, what problem it solves" data-testid="lf-desc" className={F} />
      <input value={targ} onChange={e => setTarg(e.target.value)} required maxLength={200} placeholder="Target audience (e.g. 'small farmers, hobby beekeepers')" data-testid="lf-targ" className={F} />

      <div>
        <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
          Commission you'll pay marketers on each sale: <span className="text-[#d4a34a]">{pct}%</span>
        </label>
        <input type="range" min={5} max={70} value={pct} onChange={e => setPct(parseInt(e.target.value))} data-testid="lf-pct" className="w-full accent-[#d4a34a]" />
      </div>

      {/* TEMPLATE */}
      <div>
        <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-2">
          COMMISSION TEMPLATE <span className="text-white/40">(how long the marketer keeps earning)</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {TEMPLATES.map(t => (
            <label key={t.id} data-testid={`lf-tpl-${t.id}`}
                   className={`cursor-pointer border-2 p-3 transition-colors ${tpl === t.id ? "border-[#d4a34a] bg-[#d4a34a]/10" : "border-white/15 hover:border-white/40"}`}>
              <input type="radio" name="tpl" value={t.id} checked={tpl === t.id} onChange={() => setTpl(t.id)} className="hidden" />
              <div className="flex items-center justify-between">
                <span className="font-display text-lg">{t.label}</span>
                {t.recommended && <span className="font-mono text-[9px] text-[#d4a34a]">RECOMMENDED</span>}
              </div>
              <div className="font-mono text-[11px] text-white/75 mt-1">{t.blurb}</div>
              <div className="font-mono text-[10px] text-white/50 mt-1 italic">Best for: {t.best}</div>
            </label>
          ))}
        </div>
      </div>

      {/* ATTRIBUTION */}
      <div>
        <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
          Attribution window: <span className="text-[#d4a34a]">{attrib} days</span> <span className="text-white/40">(how long a click stays warm)</span>
        </label>
        <input type="range" min={7} max={180} step={1} value={attrib} onChange={e => setAttrib(parseInt(e.target.value))} data-testid="lf-attrib" className="w-full accent-[#d4a34a]" />
      </div>

      {/* PLATFORM */}
      <div>
        <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
          SALES PLATFORM <span className="text-yt-red">*</span>
        </label>
        <select value={plat} onChange={e => setPlat(e.target.value)} data-testid="lf-plat" className={F}>
          {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {/* ONLINE-ONLY CHECK */}
      <div className="border-2 border-yt-red/40 bg-yt-red/5 p-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={verifiable} onChange={e => setVerifiable(e.target.checked)}
                 data-testid="lf-verifiable" className="mt-1 accent-[#d4a34a]" />
          <div>
            <div className="font-mono text-[11px] text-white/90">
              I confirm my product is sold through a trackable online channel (Stripe, Shopify, Gumroad, etc.), NOT cash or e-transfer.
            </div>
            <div className="font-mono text-[10px] text-white/60 mt-1">
              SmartKet can only verify online sales at launch. Offline-only businesses will be supported in a later phase.
            </div>
          </div>
        </label>
      </div>

      <button type="submit" disabled={busy || !verifiable} data-testid="lf-submit"
              className="bg-[#d4a34a] text-black font-display text-base tracking-widest px-5 py-3 shadow-brutal hover:bg-white disabled:opacity-40">
        {busy ? "posting…" : "POST TO THE BOARD"}
      </button>
    </form>
  );
}
